import { apiRequest } from './apiClient';
import { downloadReportBlob, reportClientTimeZone } from './reportApiCommon';
import type { ApiResult } from './types';
import { base64ToBlob } from '../utils/base64Blob';
import { extractReportFileFromPayload } from '../utils/pdfResponse';
import { toIsoDateEnd, toIsoDateStart } from '../utils/reportDates';

export type AuditActivityType = string;

export type AuditLogEntry = {
  id: string;
  at: string;
  title: string;
  activityType: string;
  targetEntityLabel: string;
  summary: string;
  performedBy: string;
};

export type AuditLogUser = {
  id: string;
  displayName: string;
};

export type AuditLogQuery = {
  from?: string;
  to?: string;
  activityTypes?: string[];
  performedByUserIds?: string[];
  page: number;
  rowsPerPage: number;
};

type AuditLogPageResponse = {
  rows: AuditLogEntry[];
  total: number;
  page: number;
  size: number;
};

function buildExportQuery(query: Omit<AuditLogQuery, 'page' | 'rowsPerPage'>): string {
  const qs = new URLSearchParams();
  const activityFrom = query.from ? toIsoDateStart(query.from) : undefined;
  const activityTo = query.to ? toIsoDateEnd(query.to) : undefined;
  if (activityFrom) qs.set('activityFrom', activityFrom);
  if (activityTo) qs.set('activityTo', activityTo);
  if (query.activityTypes?.length) {
    for (const type of query.activityTypes) {
      qs.append('activityTypes', type);
    }
  }
  if (query.performedByUserIds?.length) {
    for (const id of query.performedByUserIds) {
      qs.append('performedByUserIds', id);
    }
  }
  qs.set('timeZone', reportClientTimeZone());
  const serialized = qs.toString();
  return serialized ? `?${serialized}` : '';
}

export async function listAuditLogs(
  query: AuditLogQuery,
): Promise<ApiResult<{ rows: AuditLogEntry[]; total: number }>> {
  const res = await apiRequest<AuditLogPageResponse>('/v1/admin/audit-logs', {
    method: 'POST',
    body: {
      activityFrom: query.from || null,
      activityTo: query.to || null,
      activityTypes: query.activityTypes?.length ? query.activityTypes : null,
      performedByUserIds: query.performedByUserIds?.length ? query.performedByUserIds : null,
      page: query.page - 1,
      size: query.rowsPerPage,
    },
  });

  if (!res.ok) return { success: false, message: res.message };

  return {
    success: true,
    data: { rows: res.data.rows, total: res.data.total },
  };
}

export async function listAuditUsers(): Promise<ApiResult<AuditLogUser[]>> {
  const res = await apiRequest<AuditLogUser[]>('/v1/admin/audit-logs/users', { method: 'GET' });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: res.data };
}

export async function listAuditActivityTypes(): Promise<ApiResult<string[]>> {
  const res = await apiRequest<string[]>('/v1/admin/audit-logs/activity-types', { method: 'GET' });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: res.data };
}

export async function exportAuditLogsPdf(
  query: Omit<AuditLogQuery, 'page' | 'rowsPerPage'>,
): Promise<ApiResult<undefined>> {
  const res = await apiRequest<unknown>(`/v1/admin/audit-logs/export-data${buildExportQuery(query)}`, {
    method: 'GET',
  });

  if (!res.ok) return { success: false, message: res.message };

  const file =
    extractReportFileFromPayload(res.data) ?? extractReportFileFromPayload({ data: res.data });
  if (!file?.base64) {
    return { success: false, message: 'PDF content missing from export response.' };
  }

  const blob = base64ToBlob(file.base64, file.contentType || 'application/pdf');
  const fileName = file.fileName ?? `audit-logs-${new Date().toISOString().slice(0, 10)}.pdf`;
  downloadReportBlob(blob, fileName);
  return { success: true, data: undefined };
}

export async function exportAuditLogsCsv(
  query: Omit<AuditLogQuery, 'page' | 'rowsPerPage'>,
): Promise<ApiResult<undefined>> {
  const res = await apiRequest<{ text: string }>(`/v1/admin/audit-logs/export${buildExportQuery(query)}`, {
    method: 'GET',
    headers: { Accept: 'text/csv' },
  });

  if (!res.ok) return { success: false, message: res.message };

  const csvText = (res.data as { text: string }).text;
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
  downloadReportBlob(blob, 'audit-logs.csv');

  return { success: true, data: undefined };
}
